// Libraries
import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Paper,
  Typography,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import { PropTypes } from "prop-types";
import { useFormik } from "formik";

// Custom components
import SnackbarMessage from "../SnackbarMessage";

// API
import { updatePointsReminderSettings } from "../../api/Clients";

// Styles
const useStyles = makeStyles(() => ({
  paper: {
    display: "flex",
    flexDirection: "column",
    padding: "20px",
  },
}));

// Days 1-28 only: not every month has a 29th/30th/31st, so a fixed day past
// 28 would silently never fire in shorter months. "Last day of the month" is
// the separate option for that case, computed in code per month.
const DAY_OPTIONS = [...Array(28)].map((_, i) => String(i + 1));

/**
 * Main component
 * @param {object} {} Component props
 * @returns
 */
function PointsReminderSettings({ client_id, loading, settings, reloadClient }) {
  const classes = useStyles();
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState({
    message: "",
    severity: "",
    open: false,
  });

  const handleClose = () => {
    setError({
      open: false,
      message: "",
      severity: "",
    });
  };

  // Initial form data
  const initial_values = {
    assignmentReminderEnabled:
      (settings && settings.assignmentReminderEnabled) || false,
    monthlyReminderEnabled: (settings && settings.monthlyReminderEnabled) || false,
    monthlyReminderDay: (settings && settings.monthlyReminderDay) || "1",
  };

  // Manage form data
  const formik = useFormik({
    initialValues: initial_values,
    enableReinitialize: true,
    onSubmit: (values) => {
      setSaving(true);
      values.id = client_id;
      updatePointsReminderSettings(values)
        .then((xhr) => {
          if (xhr.data && xhr.data.updateClient.client) {
            setError({
              open: true,
              message: "Points email reminder settings updated successfully",
              severity: "success",
            });
            reloadClient();
          } else {
            let error_message = xhr.errors
              ? xhr.errors[0].message
              : "Points email reminder settings update failed";
            setError({
              open: true,
              message: `Error: ${error_message}`,
              severity: "error",
            });
          }
        })
        .catch((xhr) => {
          let error_message = xhr.errors
            ? xhr.errors[0].message
            : "Points email reminder settings update failed";
          setError({
            open: true,
            message: `Error: ${error_message}`,
            severity: "error",
          });
        })
        // This then() is like a "finally" block from a "try catch" :3
        .then(function () {
          setSaving(false);
        });
    },
  });

  return (
    <div>
      <Paper className={classes.paper}>
        <Typography variant="h6">Points email reminders</Typography>
        <form onSubmit={formik.handleSubmit}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.assignmentReminderEnabled}
                  onChange={formik.handleChange}
                  name="assignmentReminderEnabled"
                  disabled={loading}
                />
              }
              label="Enable reminders on points assignments"
            />
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.monthlyReminderEnabled}
                      onChange={formik.handleChange}
                      name="monthlyReminderEnabled"
                      disabled={loading}
                    />
                  }
                  label="Enable monthly reminders on the"
                />
              </Grid>
              <Grid item>
                <FormControl
                  disabled={!formik.values.monthlyReminderEnabled || loading}
                  style={{ minWidth: 160 }}
                >
                  <InputLabel>day of the month</InputLabel>
                  <Select
                    name="monthlyReminderDay"
                    value={formik.values.monthlyReminderDay}
                    onChange={formik.handleChange}
                  >
                    {DAY_OPTIONS.map((day) => (
                      <MenuItem key={day} value={day}>
                        {day}
                      </MenuItem>
                    ))}
                    <MenuItem value="last">Last day of the month</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </FormGroup>
          <Grid container justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving || loading}
            >
              Save
            </Button>
          </Grid>
        </form>
      </Paper>
      <SnackbarMessage
        open={error.open}
        severity={error.severity}
        message={error.message}
        handleClose={handleClose}
      />
    </div>
  );
}

PointsReminderSettings.propTypes = {
  client_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  loading: PropTypes.bool,
  settings: PropTypes.object,
  reloadClient: PropTypes.func.isRequired,
};

export default PointsReminderSettings;
